/**
 * ISP-9999 — force the Pinterest share `media=` image onto an ABSOLUTE https URL.
 *
 * The Share Wheel modal feeds `wheel.wheel_preview` straight into the Pinterest
 * share button's `media=` param. The backend sometimes returns `wheel_preview` as
 * a BUCKET-RELATIVE key (e.g. `20/wheel/wheel_preview-<hash>.png`) with NO scheme
 * or host. Pinterest then resolves `media=20/wheel/...png` against `pinterest.com`
 * and the pin preview image 404s — a broken pin. (Measured 2026-08-18 on web-stg,
 * bundle main.ac8d7506.js: `media=20/wheel/wheel_preview-….png`.)
 *
 * This coerces such a relative value onto the canonical wheel-media S3 origin —
 * the SAME origin the modal's existing share-image fallback already uses, so no
 * new host is introduced. A value that already carries its own scheme/host (the
 * common case) is returned VERBATIM, so a correct absolute URL is never rewritten.
 *
 * Rules (deliberately narrow so nothing else changes):
 *  - Absolute `http://` / `https://` (case-insensitive) → returned VERBATIM.
 *  - Protocol-relative `//host/path` → upgraded to `https://host/path`.
 *  - Any other non-empty relative value (`20/wheel/x.png`, `/20/wheel/x.png`) →
 *    the canonical S3 base is prepended with exactly one joining slash.
 *  - Non-string / empty input → returns `""` (never throws), so the caller can
 *    fall back to the static share image.
 *
 * @param {*} value the raw media value (typically `wheel.wheel_preview`)
 * @param {string} [base] the absolute origin to prepend (defaults to the
 *   canonical wheel-media S3 base); injectable for tests.
 * @returns {string} an absolute https URL, or `""` for empty input.
 */
export const WHEEL_MEDIA_S3_BASE =
  "https://iyuga-backend.s3.us-east-1.amazonaws.com";

export const WHEEL_SHARE_IMAGE_FALLBACK = `${WHEEL_MEDIA_S3_BASE}/wheel-share-image.jpeg`;

export function toAbsoluteMediaUrl(value, base = WHEEL_MEDIA_S3_BASE) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";

  // Already absolute (has its own scheme + host) — never rewrite it.
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // Protocol-relative `//host/path` — upgrade to https, keep the host.
  if (trimmed.startsWith("//")) return `https:${trimmed}`;

  // Relative key — prepend the canonical base with exactly one joining slash.
  const cleanBase = base.replace(/\/+$/, "");
  const cleanPath = trimmed.replace(/^\/+/, "");
  return `${cleanBase}/${cleanPath}`;
}

export default toAbsoluteMediaUrl;
